import { Telegraf, Context } from 'telegraf';
import ContentProcessor, { 
  ProcessorInput, 
  ProcessorResult, 
  CommandHandler,
  CommandContext,
  CommandResult,
  defaultLogger
} from 'weavebot-core';

export interface TelegramBotConfig {
  botToken: string;
  authorizedAdmins?: string[];
  processor?: ContentProcessor;
  enableLogging?: boolean;
}

export class TelegramBot {
  private bot: Telegraf;
  private processor: ContentProcessor;
  private config: TelegramBotConfig;

  constructor(config: TelegramBotConfig) {
    this.config = config;
    this.bot = new Telegraf(config.botToken);
    this.processor = config.processor || new ContentProcessor();

    this.setupCommands();
    this.setupErrorHandling();

    if (config.enableLogging !== false) {
      this.setupLogging();
    }
  }

  private setupCommands() {
    // Help command
    this.bot.command('help', async (ctx) => {
      await this.handleHelpCommand(ctx);
    });

    // Event processing command
    this.bot.command('event', async (ctx) => {
      await this.handleEventCommand(ctx);
    });

    // Update processing command
    this.bot.command('update', async (ctx) => {
      await this.handleUpdateCommand(ctx);
    });

    // Link processing command
    this.bot.command('link', async (ctx) => {
      await this.handleLinkCommand(ctx);
    });

    // Status command
    this.bot.command('status', async (ctx) => {
      await this.handleStatusCommand(ctx);
    });

    // Admin commands
    this.bot.command('init', async (ctx) => {
      await this.handleInitCommand(ctx);
    });

    this.bot.command('weekly_weave', async (ctx) => {
      await this.handleWeeklyWeaveCommand(ctx);
    });
  }

  private setupErrorHandling() {
    this.bot.catch((err, ctx) => {
      defaultLogger.error('Telegram bot error', { 
        error: err instanceof Error ? err.message : String(err),
        userId: ctx.from?.id?.toString(),
        chatId: ctx.chat?.id?.toString() 
      });

      ctx.reply('❌ An unexpected error occurred. Please try again later.')
        .catch(() => {}); // Ignore reply errors
    });
  }

  private setupLogging() {
    this.bot.use(async (ctx, next) => {
      const start = Date.now();
      const userId = ctx.from?.id?.toString();
      const chatId = ctx.chat?.id?.toString();
      const messageText = 'text' in ctx.message! ? ctx.message.text : '[non-text]';

      defaultLogger.info('Telegram message received', {
        userId,
        chatId,
        messageText: messageText?.substring(0, 100)
      });

      await next();

      const duration = Date.now() - start;
      defaultLogger.info('Telegram message processed', {
        userId,
        chatId,
        duration
      });
    });
  }

  // Command handlers
  private async handleHelpCommand(ctx: Context) {
    const helpMessage = `🤖 *WeaveBot Help*

*Available Commands:*

🎉 \`/event <URL>\` - Extract event information from a web page
   Example: \`/event https://www.meetup.com/some-event/\`

📰 \`/update <URL or text>\` - Process an update or article
   Examples: 
   \`/update https://blog.example.com/article\`
   \`/update This is a community announcement...\`

🔗 \`/link <URL>\` - Save a link with a one-sentence summary
   Example: \`/link https://github.com/some-project\`

📊 \`/status\` - Show bot status and configuration

📬 \`/weekly_weave\` - Generate weekly newsletter (Admin only)

❓ \`/help\` - Show this help message

*Supported Platforms:*
• Meetup.com
• Eventbrite.com  
• Facebook Events
• Luma.com
• Most event websites
• Blog articles and news sites

*How it works:*
1. Send a URL with \`/event\` or \`/update\`
2. WeaveBot scrapes the page content
3. AI extracts structured information
4. Data is saved to configured storage
5. You get a formatted summary

*Tips:*
• Make sure URLs are publicly accessible
• The bot works best with pages that have clear event details
• Processing may take 10-30 seconds for complex pages

Need help? Contact the bot administrator.`;

    await ctx.reply(helpMessage, { parse_mode: 'Markdown' });
  }

  private async handleEventCommand(ctx: Context) {
    const userId = ctx.from?.id?.toString();
    const chatId = ctx.chat?.id?.toString();

    if (!userId || !chatId) {
      return;
    }

    const text = 'text' in ctx.message! ? ctx.message.text : '';
    const args = text.split(' ').slice(1);

    if (args.length === 0) {
      await ctx.reply(
        '🤖 Please provide a URL after the /event command.\n\n' +
        'Example: `/event https://www.meetup.com/some-event/`',
        { parse_mode: 'Markdown' }
      );
      return;
    }

    const url = args[0];

    if (!this.isValidUrl(url)) {
      await ctx.reply(
        '❌ That doesn\'t look like a valid URL. Please provide a full URL starting with http or https.'
      );
      return;
    }

    // Add eyes emoji reaction to indicate processing
    await ctx.react([{ type: "emoji", emoji: "👀" }]).catch(() => {});

    try {
      const result = await this.processor.process({
        type: 'url',
        data: url,
        schema: 'event',
        options: {
          timeout: 60000 // Increase timeout to 60 seconds
        }
      }, { userId, chatId });

      if (result.success && result.data) {
        // Store in configured storage and get the record ID
        const recordId = await this.storeData(result.data, 'event');
        
        const message = this.formatEventMessage(result.data, recordId);
        await ctx.reply(message, { 
          parse_mode: 'Markdown',
          link_preview_options: { is_disabled: true }
        });

        // Remove the eyes emoji and add checkmark
        await ctx.react([{ type: "emoji", emoji: "👍" }]).catch(() => {});
      } else {
        throw new Error(result.error?.message || 'Processing failed');
      }

    } catch (error) {
      // Remove eyes emoji and add error reaction
      await ctx.react([{ type: "emoji", emoji: "👎" }]).catch(() => {});
      
      const errorMessage = this.formatErrorMessage(
        (error as Error).message,
        url
      );
      
      await ctx.reply(errorMessage, { 
        parse_mode: 'Markdown',
        link_preview_options: { is_disabled: true }
      });
    }
  }

  private async handleUpdateCommand(ctx: Context) {
    const userId = ctx.from?.id?.toString();
    const chatId = ctx.chat?.id?.toString();

    if (!userId || !chatId) {
      return;
    }

    const text = 'text' in ctx.message! ? ctx.message.text : '';
    const args = text.split(' ').slice(1);

    if (args.length === 0) {
      await ctx.reply(
        '🤖 Please provide a URL or text after the /update command.\n\n' +
        'Examples:\n' +
        '`/update https://blog.example.com/article`\n' +
        '`/update This is a community announcement...`',
        { parse_mode: 'Markdown' }
      );
      return;
    }

    const input = args.join(' ');
    const isUrl = this.isValidUrl(input);

    // Add eyes emoji reaction to indicate processing
    await ctx.react([{ type: "emoji", emoji: "👀" }]).catch(() => {});

    try {
      const result = await this.processor.process({
        type: isUrl ? 'url' : 'text',
        data: input,
        schema: 'update'
      }, { userId, chatId });

      if (result.success && result.data) {
        // Store in configured storage and get the record ID
        const recordId = await this.storeData(result.data, 'update');
        
        const message = this.formatUpdateMessage(result.data, recordId);
        await ctx.reply(message, { 
          parse_mode: 'Markdown',
          link_preview_options: { is_disabled: true }
        });

        // Remove the eyes emoji and add checkmark
        await ctx.react([{ type: "emoji", emoji: "👍" }]).catch(() => {});
      } else {
        throw new Error(result.error?.message || 'Processing failed');
      }

    } catch (error) {
      // Remove eyes emoji and add error reaction
      await ctx.react([{ type: "emoji", emoji: "👎" }]).catch(() => {});
      
      const errorMessage = this.formatErrorMessage(
        (error as Error).message,
        isUrl ? input : undefined
      );
      
      await ctx.reply(errorMessage, { 
        parse_mode: 'Markdown',
        link_preview_options: { is_disabled: true }
      });
    }
  }

  private async handleLinkCommand(ctx: Context) {
    const userId = ctx.from?.id?.toString();
    const chatId = ctx.chat?.id?.toString();

    if (!userId || !chatId) {
      return;
    }

    const text = 'text' in ctx.message! ? ctx.message.text : '';
    const args = text.split(' ').slice(1);

    if (args.length === 0) {
      await ctx.reply(
        '🔗 Please provide a URL after the /link command.\n\n' +
        'Example: `/link https://example.com`',
        { parse_mode: 'Markdown' }
      );
      return;
    }

    const url = args[0];

    if (!this.isValidUrl(url)) {
      await ctx.reply(
        '❌ That doesn\'t look like a valid URL. Please provide a full URL starting with http or https.'
      );
      return;
    }

    // Add eyes emoji reaction to indicate processing
    await ctx.react([{ type: "emoji", emoji: "👀" }]).catch(() => {});

    try {
      const result = await this.processor.process({
        type: 'url',
        data: url,
        schema: 'link',
        options: {
          timeout: 60000 // Increase timeout to 60 seconds
        }
      }, { userId, chatId });

      if (result.success && result.data) {
        // Store in configured storage and get the record ID
        const recordId = await this.storeData(result.data, 'link');
        
        const message = this.formatLinkMessage(result.data, recordId);
        await ctx.reply(message, { 
          parse_mode: 'Markdown',
          link_preview_options: { is_disabled: true }
        });

        // Remove the eyes emoji and add checkmark
        await ctx.react([{ type: "emoji", emoji: "👍" }]).catch(() => {});
      } else {
        throw new Error(result.error?.message || 'Processing failed');
      }

    } catch (error) {
      // Remove eyes emoji and add error reaction
      await ctx.react([{ type: "emoji", emoji: "👎" }]).catch(() => {});
      
      const errorMessage = this.formatErrorMessage(
        (error as Error).message,
        url
      );
      
      await ctx.reply(errorMessage, { 
        parse_mode: 'Markdown',
        link_preview_options: { is_disabled: true }
      });
    }
  }

  private async handleStatusCommand(ctx: Context) {
    const processors = this.processor.listProcessors();
    const storageAdapters = this.processor.listStorageAdapters();
    const schemas = this.processor.listSchemas();

    const status = `📊 *Bot Status*

*Processors:* ${processors.length > 0 ? processors.join(', ') : 'None'}
*Storage:* ${storageAdapters.length > 0 ? storageAdapters.join(', ') : 'None'}
*Schemas:* ${schemas.length > 0 ? schemas.join(', ') : 'None'}

*Bot Version:* 1.0.0
*Status:* ✅ Running`;

    await ctx.reply(status, { parse_mode: 'Markdown' });
  }

  private async handleInitCommand(ctx: Context) {
    const userId = ctx.from?.id?.toString();

    // Check admin authorization
    if (this.config.authorizedAdmins && 
        this.config.authorizedAdmins.length > 0 && 
        !this.config.authorizedAdmins.includes(userId!)) {
      await ctx.reply('🔒 You are not authorized to initialize Airtable configuration.');
      return;
    }

    await ctx.reply('🔄 Initializing Airtable configuration...\n\nDiscovering tables and setting up configuration...');

    try {
      // Get the Airtable storage adapter
      const storageAdapters = this.processor.listStorageAdapters();
      if (storageAdapters.length === 0) {
        throw new Error('No Airtable storage adapter configured');
      }

      const storage = this.processor.getStorage(storageAdapters[0]);
      if (!storage || !('initializeSetup' in storage)) {
        throw new Error('Airtable storage adapter does not support initialization');
      }

      // Initialize and get setup
      const setup = await (storage as any).initializeSetup();
      
      // Apply the setup to the storage adapter
      (storage as any).applySetup(setup);

      // Save configuration to file
      await this.saveAirtableConfig(setup);

      let message = `✅ *Airtable Initialization Complete*

📊 *Events Table*
• ID: \`${setup.eventsTable.id}\`
• View: \`${setup.eventsTable.primaryViewId}\`
• Fields: ${setup.eventsTable.fields.length} configured

📰 *Updates Table*  
• ID: \`${setup.updatesTable.id}\`
• View: \`${setup.updatesTable.primaryViewId}\`
• Fields: ${setup.updatesTable.fields.length} configured`;

      if (setup.linksTable) {
        message += `

🔗 *Links Table*
• ID: \`${setup.linksTable.id}\`
• View: \`${setup.linksTable.primaryViewId}\`
• Fields: ${setup.linksTable.fields.length} configured`;
      }

      message += `

💾 Configuration saved to \`airtable-config.json\`

🎉 WeaveBot is now ready to process events, updates, and links with working Airtable links!`;

      await ctx.reply(message, { parse_mode: 'Markdown' });

    } catch (error) {
      const errorMessage = this.formatErrorMessage(
        (error as Error).message,
        undefined
      );
      
      await ctx.reply(errorMessage, { 
        parse_mode: 'Markdown',
        link_preview_options: { is_disabled: true }
      });
    }
  }

  private async handleWeeklyWeaveCommand(ctx: Context) {
    const userId = ctx.from?.id?.toString();

    // Check admin authorization
    if (this.config.authorizedAdmins && 
        this.config.authorizedAdmins.length > 0 && 
        !this.config.authorizedAdmins.includes(userId!)) {
      await ctx.reply('🔒 You are not authorized to generate newsletters.');
      return;
    }

    await ctx.reply(
      '📰 Newsletter generation is not yet implemented in the library version.\n\n' +
      'This feature will be available in a future release.'
    );
  }

  private async saveAirtableConfig(setup: any): Promise<void> {
    try {
      const fs = await import('fs/promises');
      const path = await import('path');
      const configPath = path.join(process.cwd(), 'airtable-config.json');
      
      const configData = JSON.stringify(setup, null, 2);
      await fs.writeFile(configPath, configData, 'utf-8');
      
      defaultLogger.info('Airtable configuration saved', { path: configPath });
    } catch (error) {
      defaultLogger.error('Failed to save Airtable configuration', { error: (error as Error).message });
      throw new Error(`Failed to save configuration: ${(error as Error).message}`);
    }
  }

  // Utility methods
  private isValidUrl(string: string): boolean {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  }

  private formatEventMessage(event: any, recordId?: string): string {
    const airtableLink = recordId ? this.getAirtableRecordLink('Events', recordId) : null;
    
    let message = `✅ *Event Processed Successfully*

*${event.title}*

📅 *Date:* ${this.formatDate(event.startDateTime)}
📍 *Location:* ${event.location?.venue || event.location?.address || 'TBD'}
🏢 *Organizer:* ${event.organizer?.name || 'TBD'}
🎟️ *Price:* ${event.ticketPrice?.isFree ? 'Free' : (event.ticketPrice?.amount ? `${event.ticketPrice.currency} ${event.ticketPrice.amount}` : 'TBD')}
🌐 *Online:* ${event.isOnline ? 'Yes' : 'No'}

📝 *Description:*
${event.description?.substring(0, 300)}${event.description?.length > 300 ? '...' : ''}

*Event saved to database* ✅`;
    
    if (airtableLink) {
      message += `

[📊 View in Airtable](${airtableLink})`;
    }
    
    return message;
  }

  private formatUpdateMessage(update: any, recordId?: string): string {
    const airtableLink = recordId ? this.getAirtableRecordLink('Updates', recordId) : null;
    
    let message = `✅ *Update Processed Successfully*

*${update.title}*

👤 *Author:* ${update.author || 'Unknown'}
📂 *Category:* ${update.category}
⭐ *Relevance:* ${update.relevanceScore}/10

📝 *Summary:*
${update.summary}

*Update saved to database* ✅`;
    
    if (airtableLink) {
      message += `

[📊 View in Airtable](${airtableLink})`;
    }
    
    return message;
  }

  private formatLinkMessage(link: any, recordId?: string): string {
    const airtableLink = recordId ? this.getAirtableRecordLink('Links', recordId) : null;
    
    let message = `✅ *Link Saved Successfully*

🔗 *URL:* ${link.url}

📝 *Title:* ${link.title}

💬 *Summary:* _${link.summary}_

*Link saved to database* ✅`;
    
    if (airtableLink) {
      message += `

[📊 View in Airtable](${airtableLink})`;
    }
    
    return message;
  }

  private formatErrorMessage(error: string, url?: string): string {
    let message = `❌ *Processing Failed*\n\n`;
    
    if (url) {
      message += `*URL:* ${url}\n\n`;
    }
    
    message += `*Error:* ${error}\n\n`;
    message += `🔧 *Troubleshooting:*\n`;
    message += `• Make sure the URL is publicly accessible\n`;
    message += `• Check if the page contains event/update information\n`;
    message += `• Try again in a few moments\n`;
    message += `• Contact administrator if the problem persists`;

    return message;
  }

  private formatDate(dateString: string): string {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  }

  private async storeData(data: unknown, schema: string): Promise<string | undefined> {
    try {
      const storageAdapters = this.processor.listStorageAdapters();
      if (storageAdapters.length > 0) {
        const storage = this.processor.getStorage(storageAdapters[0]);
        if (storage) {
          const result = await storage.create(data, schema);
          defaultLogger.info('Data stored successfully', { schema, recordId: result?.id });
          return result?.id;
        }
      }
    } catch (error) {
      defaultLogger.error('Failed to store data', { 
        error: (error as Error).message, 
        schema 
      });
      // Don't throw - storage failure shouldn't break the command
    }
    return undefined;
  }

  private getAirtableRecordLink(tableName: string, recordId: string): string | null {
    try {
      // Map table name to schema name
      const schemaMap: Record<string, string> = {
        'Events': 'event',
        'Updates': 'update',
        'Links': 'link'
      };
      
      const schema = schemaMap[tableName];
      if (!schema) {
        defaultLogger.warn('Unknown table name for Airtable link', { tableName });
        return null;
      }

      // Get Airtable base ID from storage adapter
      const storageAdapters = this.processor.listStorageAdapters();
      if (storageAdapters.length > 0) {
        const storage = this.processor.getStorage(storageAdapters[0]);
        if (storage && 'getBaseId' in storage && 'getTableId' in storage && 'getViewId' in storage) {
          const baseId = (storage as any).getBaseId();
          const tableId = (storage as any).getTableId(schema);
          const viewId = (storage as any).getViewId(schema);
          return `https://airtable.com/${baseId}/${tableId}/${viewId}/${recordId}?blocks=hide`;
        }
      }
    } catch (error) {
      defaultLogger.warn('Could not generate Airtable link', { error: (error as Error).message });
    }
    return null;
  }

  // Public methods
  async start(): Promise<void> {
    defaultLogger.info('Starting Telegram bot...');
    
    try {
      await this.bot.launch();
      defaultLogger.info('Telegram bot started successfully');
      
      // Graceful shutdown
      process.once('SIGINT', () => this.stop());
      process.once('SIGTERM', () => this.stop());
      
    } catch (error) {
      defaultLogger.error('Failed to start Telegram bot', { 
        error: (error as Error).message 
      });
      throw error;
    }
  }

  async stop(): Promise<void> {
    defaultLogger.info('Stopping Telegram bot...');
    this.bot.stop();
    defaultLogger.info('Telegram bot stopped');
  }

  // Add custom command handler
  addCommand(command: string, handler: (ctx: Context) => Promise<void>): void {
    this.bot.command(command, handler);
  }

  // Get underlying bot instance for advanced usage
  getBot(): Telegraf {
    return this.bot;
  }

  // Get processor instance
  getProcessor(): ContentProcessor {
    return this.processor;
  }
}